import { useCallback } from 'react';

import { useAuth } from '@/components/auth/AuthProvider';
import type { AnswerStyle } from '@/components/chat/AnswerStyle';
import { answerStyleStore } from '@/hooks/useAnswerStyle';
import useAnalytics from '@/hooks/useAnalytics';
import useApi from '@/hooks/useApi';

/** Where the editor was opened from, for `Answer Style Editor Opened` / `Saved`. */
export type AnswerStyleSource = 'invite' | 'settings' | 'composer' | 'signup';

/** How the invitation was retired, for `Answer Style Invite Dismissed`. */
export type AnswerStyleDismissMethod = 'not_now' | 'closed' | 'chat_started';

/** Where a successful save landed — drives the confirmation wording. */
export type AnswerStyleSaveResult = 'account' | 'browser' | 'memory';

/**
 * Save and skip actions shared by the onboarding dialog, Response options, and
 * Settings. A signed-in save is confirmed by the server before anything
 * changes; a guest save lands in this browser (or, with storage blocked, in
 * memory for the visit). Mirrors the mobile hook of the same name.
 *
 * Analytics carry the enum values and where the action came from — never
 * message text, an address, or anything inferred about the reader.
 */
export default function useAnswerStyleActions() {
  const { user } = useAuth();
  const query = useApi();
  const analytics = useAnalytics();
  const accountId = user?.user_id ?? user?.id;

  const save = useCallback(
    async (style: AnswerStyle, source: AnswerStyleSource): Promise<AnswerStyleSaveResult> => {
      const before = answerStyleStore.getSnapshot();
      let result: AnswerStyleSaveResult;
      if (user && accountId) {
        await answerStyleStore.saveAccount(accountId, style, async () => {
          await query('/user/settings', {
            method: 'POST',
            body: JSON.stringify({ email: user.email, answer_style: style }),
          });
        });
        result = 'account';
      } else {
        result = await answerStyleStore.saveGuest(style);
      }
      analytics.track('Answer Style Saved', {
        wording: style.wording,
        length: style.length,
        previousWording: before.style.wording,
        previousLength: before.style.length,
        source,
        authed: !!user,
        firstChoice: !before.explicit,
      });
      return result;
    },
    [accountId, analytics, query, user]
  );

  /**
   * Retire the invitation. `source` is the entry point, as on the other
   * events; `method` is how: "Not now", closing the editor, or sending a
   * message past it (`chat_started`).
   */
  const dismissInvite = useCallback(
    (source: AnswerStyleSource, method: AnswerStyleDismissMethod) => {
      const before = answerStyleStore.getSnapshot();
      if (before.invite || before.explicit) return;
      analytics.track('Answer Style Invite Dismissed', { source, method });
      if (user && accountId) {
        answerStyleStore.dismissAccountInvite(accountId);
        // Best-effort: a failed write only means the invitation may appear once
        // more on another device.
        void query('/user/settings', {
          method: 'POST',
          body: JSON.stringify({ email: user.email, answer_style_invite: 'dismissed' }),
        }).catch(() => undefined);
      } else {
        void answerStyleStore.dismissGuestInvite();
      }
    },
    [accountId, analytics, query, user]
  );

  return { save, dismissInvite, signedIn: !!user };
}
